export const AU_QUALIFYING_SCORE_THRESHOLD = 10;
export const APNEALINK_QUALIFYING_SCORE_THRESHOLD = 42;
export const APNEALINK_MAX_SCORE = 70;
export const AU_MAX_SCORE = 34;

type SurveyData = Record<string, any>;

export type ApneaScores = {
  bmi: number | null;
  apnealinkScore: number;
  auScore: number;
  qualifiedResmedApnealink: boolean;
  qualifiedAuOsa: boolean;
  qualifiedOsaRisk: boolean;
};

const yes = (value: unknown) => value === 'Yes';

const parseNumber = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isAgeAtLeast50 = (age: unknown) => age === '50s' || age === '60+';

const calculateBmi = (data: SurveyData): number | null => {
  const feet = parseNumber(data.height_feet);
  const inches = parseNumber(data.height_inches);
  const weightPounds = parseNumber(data.weight_pounds);

  if (feet === null || inches === null || weightPounds === null) {
    return null;
  }

  const totalInches = feet * 12 + inches;
  if (totalInches <= 0 || weightPounds <= 0) {
    return null;
  }

  return (weightPounds / (totalInches * totalInches)) * 703;
};

const getApnealinkBmiPoints = (bmi: number | null): number => {
  if (bmi === null || bmi < 25) return 0;
  if (bmi < 30) return 6;
  return 12;
};

const getAuBmiPoints = (bmi: number | null): number => {
  if (bmi === null || bmi < 25) return 0;
  if (bmi < 30) return 4;
  if (bmi < 35) return 8;
  if (bmi < 40) return 12.5;
  return 15.5;
};

const getPainPoints = (painFrequency: unknown): number =>
  painFrequency === 'Once or twice a week' || painFrequency === 'Three or more times a week' ? 1.5 : 0;

export const calculateApneaScores = (data: SurveyData): ApneaScores => {
  const bmi = calculateBmi(data);
  const snorePoints = yes(data.apnea_snore) ? 17 : 0;
  const agePoints = isAgeAtLeast50(data.age) ? 14 : 0;
  const genderPoints = data.gender === 'Male' ? 27 : 0;
  const apnealinkScore = getApnealinkBmiPoints(bmi) + snorePoints + agePoints + genderPoints;

  const auScore =
    getAuBmiPoints(bmi) +
    (data.gender === 'Male' ? 3 : 0) +
    (isAgeAtLeast50(data.age) ? 6 : 0) +
    getPainPoints(data.apnea_sleep_pain_frequency) +
    (yes(data.apnea_snore) ? 1 : 0) +
    (yes(data.apnea_snore) && yes(data.apnea_dry_mouth) ? 1 : 0) +
    (yes(data.apnea_morning_headaches) ? 1 : 0) +
    (yes(data.apnea_daytime_sleepy) ? 1 : 0) +
    (yes(data.apnea_stop_breathing) ? 3 : 0) +
    (yes(data.apnea_gasping) ? 1 : 0);

  const qualifiedResmedApnealink = apnealinkScore >= APNEALINK_QUALIFYING_SCORE_THRESHOLD;
  const qualifiedAuOsa = auScore >= AU_QUALIFYING_SCORE_THRESHOLD;

  return {
    bmi,
    apnealinkScore,
    auScore,
    qualifiedResmedApnealink,
    qualifiedAuOsa,
    qualifiedOsaRisk: qualifiedResmedApnealink || qualifiedAuOsa,
  };
};

/**
 * Single source of truth for the high-OSA-risk gate (Australia score ≥ 10), used
 * to decide whether the SleepioRx intake shows the OSA follow-up questions and
 * whether the `osaRisk.followUp` payload is sent. Mirrors nyx `getIsHighOsaRisk`.
 *
 * Fail-closed: any uncertainty → false (never block / never spuriously prompt).
 * BMI is null until height + weight are entered, which doubles as the
 * "apnea survey not completed" sentinel.
 */
export const getIsHighOsaRisk = (data: SurveyData): boolean => {
  try {
    const scores = calculateApneaScores(data);
    return scores.bmi !== null && scores.auScore >= AU_QUALIFYING_SCORE_THRESHOLD;
  } catch {
    return false;
  }
};
